import { Drawer, Group, Stack, Title } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconPlus } from "@tabler/icons-react"
import { ButtonWithMenu, ButtonWithMenuProps, InfoBox, Loader } from "@tfg/ui"
import { zod4Resolver } from "mantine-form-zod-resolver"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import z from "zod"

import { queryClient } from "~/backend/queryClient"
import { HeaderControlsProvider } from "~/components/contexts/useHeaderControlsContext"
import { useCustomReactTable } from "~/components/hooks/useCustomReactTable"
import { CustomTable } from "~/components/table/CustomTable"
import {
  findFeatureFlagsQueryKey,
  useCreateFeatureFlag,
  useFindFeatureFlags,
  useUpdateFeatureFlag,
} from "~/gen/api"
import type { CreateFeatureFlagRequestDto } from "~/gen/api"
import type { FindFeatureFlagsResponseItemDto } from "~/gen/api"
import { TypesForm } from "~/pages/Admin/components/TypesForm"
import type {
  FormConfig,
  FormFieldConfig,
} from "~/pages/Admin/components/form.types"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

import { useColumns } from "./hooks/columns"

type CreateFeatureFlagFormValues = {
  name: string
  description: string
  category: string
  defaultState: boolean
  ttlDays: string
}

const DEFAULT_CREATE_VALUES: CreateFeatureFlagFormValues = {
  name: "",
  description: "",
  category: "",
  defaultState: false,
  ttlDays: "",
}

const FeatureFlagsAdminContent = (): JSX.Element => {
  const { t } = useTranslation(["admin", "common"])
  const [drawerOpen, drawerActions] = useDisclosure(false)
  const [updatingFeatureFlagName, setUpdatingFeatureFlagName] = useState<
    string | null
  >(null)
  const createFeatureFlagSchema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .trim()
          .min(1, t("featureFlags.validation.nameRequired")),
        description: z
          .string()
          .trim()
          .min(1, t("featureFlags.validation.descriptionRequired")),
        category: z
          .string()
          .trim()
          .min(1, t("featureFlags.validation.categoryRequired")),
        defaultState: z.boolean(),
        ttlDays: z
          .string()
          .refine(
            (value) => value.trim().length === 0 || /^\d+$/.test(value.trim()),
            t("featureFlags.validation.ttlDaysInteger")
          ),
      }),
    [t]
  )
  const formConfig = useMemo<FormConfig<CreateFeatureFlagFormValues>>(
    () => ({
      validate: zod4Resolver(createFeatureFlagSchema),
      initialValues: DEFAULT_CREATE_VALUES,
    }),
    [createFeatureFlagSchema]
  )
  const fields = useMemo<FormFieldConfig[]>(
    () => [
      {
        type: "text",
        name: "name",
        label: t("featureFlags.form.name.label"),
        placeholder: t("featureFlags.form.name.placeholder"),
        required: true,
      },
      {
        type: "text",
        name: "description",
        label: t("featureFlags.form.description.label"),
        placeholder: t("featureFlags.form.description.placeholder"),
        required: true,
      },
      {
        type: "text",
        name: "category",
        label: t("featureFlags.form.category.label"),
        placeholder: t("featureFlags.form.category.placeholder"),
        required: true,
      },
      {
        type: "text",
        name: "ttlDays",
        label: t("featureFlags.form.ttlDays.label"),
        placeholder: t("featureFlags.form.ttlDays.placeholder"),
      },
      {
        type: "switch",
        name: "defaultState",
        label: t("featureFlags.form.defaultState.label"),
        tooltipJustify: "start",
        tooltip: t("featureFlags.form.defaultState.tooltip"),
        placeholder: "",
      },
    ],
    [t]
  )

  const { data, isLoading, isError, isFetching } = useFindFeatureFlags()
  const featureFlags = data?.data.data ?? []

  const { mutate: updateFeatureFlag } = useUpdateFeatureFlag({
    mutation: {
      onMutate: ({ name }) => {
        setUpdatingFeatureFlagName(name)
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: findFeatureFlagsQueryKey(),
        })
      },
      onError: (error) => {
        showAxiosError({
          error,
          defaultMessage: t("featureFlags.feedback.updateError"),
          showNotification: true,
        })
      },
      onSettled: () => {
        setUpdatingFeatureFlagName(null)
      },
    },
  })

  const { mutate: createFeatureFlag } = useCreateFeatureFlag({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: findFeatureFlagsQueryKey(),
        })
        drawerActions.close()
        showSuccessNotification({
          title: t("featureFlags.feedback.createSuccessTitle"),
          message: t("featureFlags.feedback.createSuccessMessage"),
        })
      },
      onError: (error) => {
        showAxiosError({
          error,
          defaultMessage: t("featureFlags.feedback.createError"),
          showNotification: true,
        })
      },
    },
  })

  const handleCreateFeatureFlag = (
    values: CreateFeatureFlagFormValues
  ): void => {
    const requestData: CreateFeatureFlagRequestDto = {
      name: values.name.trim(),
      description: values.description.trim(),
      category: values.category.trim(),
      defaultState: values.defaultState,
      ttlDays: values.ttlDays.trim().length === 0 ? undefined : values.ttlDays,
    }

    createFeatureFlag({ data: requestData })
  }

  const handleToggleState = (
    featureFlag: FindFeatureFlagsResponseItemDto
  ): void => {
    updateFeatureFlag({
      name: featureFlag.name,
      data: {
        state: !featureFlag.state,
      },
    })
  }

  const columns = useColumns({
    updatingFeatureFlagName,
    onToggleState: handleToggleState,
  })
  const actions = useMemo<ButtonWithMenuProps["items"]>(
    () => [
      {
        label: t("featureFlags.actions.create"),
        icon: <IconPlus />,
        onClick: drawerActions.open,
      },
    ],
    [drawerActions.open, t]
  )
  const table = useCustomReactTable({
    data: featureFlags,
    columns,
  })

  if (isError) {
    return (
      <InfoBox variant="error" message={t("featureFlags.feedback.loadError")} />
    )
  }

  return (
    <Stack>
      <Group justify="right">
        <ButtonWithMenu items={actions}>
          {t("featureFlags.actions.menu")}
        </ButtonWithMenu>
      </Group>

      {isLoading ? (
        <Loader fullPage />
      ) : (
        <CustomTable
          table={table}
          manualPaginationCount={featureFlags.length}
          isFetching={isFetching}
        />
      )}

      <Drawer
        position="right"
        opened={drawerOpen}
        onClose={drawerActions.close}
        title={<Title order={2}>{t("featureFlags.drawer.title")}</Title>}
        size="lg"
      >
        <TypesForm
          elements={fields}
          submitLabel={t("featureFlags.form.submit")}
          cancelLabel={t("featureFlags.form.cancel")}
          formConfig={formConfig}
          mutate={handleCreateFeatureFlag}
          closeDrawer={drawerActions.close}
        />
      </Drawer>
    </Stack>
  )
}

export const FeatureFlagsAdmin = (): JSX.Element => {
  return (
    <HeaderControlsProvider>
      <FeatureFlagsAdminContent />
    </HeaderControlsProvider>
  )
}
